import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '../../../services/api.service';
import { AuthService } from '../../../services/auth.service';
import { LoggerService } from '../../../services/logger.service';
import { RealtimeEventsService } from '../../../services/realtime-events.service';
import { unwrapOrderFromApiResponse } from '../../../shared/utils/order-api.util';
import {
  buildTapToPayNativeParams,
  confirmTapToPayPaymentMode,
  resolveInternalOrderId,
  tapToPayOrderIdsMatch,
} from '../../../shared/utils/tap-to-pay-order-params.util';
import { PaymentMethodCode } from '../../../shared/types/payment-method.enum';
import {
  TapToPayAndroidBridgeService,
  TapToPayAndroidPaymentDetail,
} from '../../../services/tap-to-pay-android-bridge.service';
import { TapToPayAndroidNativeService } from '../../../services/tap-to-pay-android-native.service';
import { bindPaymentSseEvents } from '../../../orders/utils/payment-realtime-sse.util';

export interface TapToPayContext {
  amount: string;
  phoneNumber: string;
  email: string;
  locationId: string;
  adminUserId: string;
  washType: string;
  packageId: string;
  vehicleId: string;
  stripeAccountId?: string;
  authToken?: string;
}

export interface TapToPayPaymentEvent {
  orderId: string;
  data: any;
}

/**
 * Service that encapsulates all Tap-to-Pay deep link building,
 * SSE payment event subscription, and payment status polling.
 * Provided at the component level.
 */
@Injectable()
export class TapToPayService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  readonly paymentSuccess$ = new Subject<TapToPayPaymentEvent>();
  readonly paymentFailed$ = new Subject<{ orderId: string; message: string }>();

  private currentTapToPayLocationId: string | null = null;
  private userPaymentSseSub: Subscription | null = null;
  private locationPaymentSseSub: Subscription | null = null;
  private pollingInterval: any = null;
  private pollingTimeout: any = null;
  private processedPayments = new Set<string>();
  private tapToPayHoldOverlayUntilSuccess = false;
  /** `orderId` query param on the last Tap to Pay deep link (may differ from internal `paymentOrderId`). */
  private lastDeepLinkOrderIdParam: string | null = null;

  isGoingToPayment = false;
  paymentOrderId: string | null = null;
  taptopayLoading = false;

  private readonly realtime = inject(RealtimeEventsService);
  private readonly ngZone = inject(NgZone);

  constructor(
    private readonly apiService: ApiService,
    private readonly authService: AuthService,
    private readonly logger: LoggerService,
    private readonly tapToPayAndroidBridge: TapToPayAndroidBridgeService,
    private readonly tapToPayAndroidNative: TapToPayAndroidNativeService
  ) {
    this.tapToPayAndroidBridge.nativePaymentResults$
      .pipe(takeUntil(this.destroy$))
      .subscribe(detail => {
        if (detail.status === 'success') {
          this.onAndroidTapToPayPaymentSuccess(detail);
        } else if (detail.status === 'failed') {
          this.onAndroidTapToPayPaymentFailed(detail);
        }
      });
  }

  // ── Deep Link ──────────────────────────────────────────────────────────────

  openWithOrderData(apiOrder: any, ctx: TapToPayContext): void {
    const params = this.buildOrderParams(apiOrder, ctx);
    this.launchDeepLink(params, apiOrder?.id ?? null, apiOrder?.location_id ?? ctx.locationId);
  }

  openWithFormData(ctx: TapToPayContext): void {
    const params = this.buildFormParams(ctx);
    this.isGoingToPayment = true;
    this.paymentOrderId = null;
    this.lastDeepLinkOrderIdParam =
      (params.get('orderId') ?? '').replace(/^#+/, '').trim() || null;
    this.sendTapToPayToNativeApp(params);
  }

  /**
   * Attempts to load an order by ID and open the tap-to-pay flow with the retrieved order data, or falls back to form data on error.
   * @example
   * etryWithOrderId('12345', ctx)
   * void
   * @param {string} orderId - The order identifier used to fetch order details.
   * @param {TapToPayContext} ctx - The tap-to-pay context used when opening the order or form flow.
   * @returns {void} Does not return a value.
   **/
  retryWithOrderId(orderId: string, ctx: TapToPayContext): void {
    this.apiService
      .get(`order/${orderId}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const order = unwrapOrderFromApiResponse(res) as any;
          this.openWithOrderData(order, ctx);
        },
        error: () => this.openWithFormData(ctx),
      });
  }

  /**
   * After Tap to Pay fails, reset `payment_status` to pending then reopen the app (legacy modal behaviour).
   */
  resetPaymentStatusToPendingThenRetry(orderId: string, ctx: TapToPayContext): void {
    this.apiService
      .put(`order/${orderId}`, { payment_status: 'pending' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.retryWithOrderId(orderId, ctx),
        error: () => this.retryWithOrderId(orderId, ctx),
      });
  }

  resetPaymentTracking(): void {
    this.tapToPayHoldOverlayUntilSuccess = false;
    this.isGoingToPayment = false;
    this.paymentOrderId = null;
    this.lastDeepLinkOrderIdParam = null;
    this.taptopayLoading = false;
    this.stopStatusPolling();
    this.disconnectTapToPayPusher();
  }

  /** Call after parent shows payment success UI; releases Tap to Pay full-screen overlay. */
  finishTapToPaySuccessUi(): void {
    if (this.tapToPayHoldOverlayUntilSuccess) {
      this.tapToPayHoldOverlayUntilSuccess = false;
      this.taptopayLoading = false;
    }
    this.isGoingToPayment = false;
    this.paymentOrderId = null;
    this.disconnectTapToPayPusher();
  }

  // ── Realtime (SSE payment events) ────────────────────────────────────────────

  /**
   * Initializes payment realtime for a user via SSE payment events.
   */
  initUserPusher(_userId: string): void {
    this.disconnectUserPusher();
    this.userPaymentSseSub = bindPaymentSseEvents(this.realtime, this.ngZone, {
      onSuccess: (event, data) => this.handleSuccess(event, data),
      onFailed: (event, data) => this.handleFailure(event, data),
      skipNamedInCatchAll: true,
    });
  }

  /**
   * Subscribes for tap-to-pay payment updates for a location via SSE, filtered by order.
   */
  subscribePusherForLocation(locationId: string, _orderId: string): void {
    if (!locationId || (this.currentTapToPayLocationId === locationId && this.locationPaymentSseSub)) {
      return;
    }
    this.disconnectTapToPayPusher();
    this.currentTapToPayLocationId = locationId;

    this.locationPaymentSseSub = bindPaymentSseEvents(this.realtime, this.ngZone, {
      onSuccess: (_event, d) => {
        if (!this.isGoingToPayment) return;
        const pid = d?.payment_intent_id ?? d?.order_id;
        if (pid && this.paymentOrderId && pid.toString() === this.paymentOrderId.toString()) {
          this.tapToPayHoldOverlayUntilSuccess = true;
          this.stopStatusPolling(false);
          this.emitPaymentSuccess(this.paymentOrderId ?? pid.toString(), d);
        }
      },
      onFailed: (_event, d) => {
        if (!this.isGoingToPayment) return;
        const pid = d?.order_id ?? d?.orderId;
        if (pid && this.paymentOrderId && pid.toString() === this.paymentOrderId.toString()) {
          this.tapToPayHoldOverlayUntilSuccess = false;
          this.stopStatusPolling();
          this.paymentFailed$.next({ orderId: pid.toString(), message: d?.message ?? '' });
        }
      },
      skipNamedInCatchAll: true,
    });
  }

  disconnectUserPusher(): void {
    this.userPaymentSseSub?.unsubscribe();
    this.userPaymentSseSub = null;
  }

  /**
   * Disconnects location payment SSE subscription.
   * Does not tear down the shared user SSE stream.
   */
  disconnectTapToPayPusher(): void {
    this.locationPaymentSseSub?.unsubscribe();
    this.locationPaymentSseSub = null;
    this.currentTapToPayLocationId = null;
  }

  // ── Polling ────────────────────────────────────────────────────────────────

  startStatusPolling(orderId: string, intervalMs = 5000, timeoutMs = 120000): void {
    this.stopStatusPolling();
    this.pollingInterval = setInterval(() => this.pollOrderStatus(orderId), intervalMs);
    this.pollingTimeout = setTimeout(() => this.stopStatusPolling(), timeoutMs);
  }

  /**
   * Stops any active top status polling timers and optionally hides the tap-to-pay overlay.
   * @example
   * topStatusPolling(true)
   * void
   * @param {boolean} hideTapOverlay - Whether to hide the tap-to-pay loading and hold overlay state.
   * @returns {void} No return value.
   */
  stopStatusPolling(hideTapOverlay = true): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    if (this.pollingTimeout) {
      clearTimeout(this.pollingTimeout);
      this.pollingTimeout = null;
    }
    if (hideTapOverlay) {
      this.taptopayLoading = false;
      this.tapToPayHoldOverlayUntilSuccess = false;
    }
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnDestroy(): void {
    this.stopStatusPolling();
    this.disconnectUserPusher();
    this.disconnectTapToPayPusher();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  /**
   * WebView embedded in Tap to Pay Android: native app calls `onTapToPayAndroidPaymentResult` immediately.
   */
  private onAndroidTapToPayPaymentSuccess(detail: TapToPayAndroidPaymentDetail): void {
    if (!this.isGoingToPayment || detail.status !== 'success') {
      return;
    }
    const incoming = (detail.order_id ?? detail.internal_id ?? '').replace(/^#+/, '').trim();
    if (!incoming) {
      return;
    }
    if (
      !tapToPayOrderIdsMatch(incoming, [
        this.paymentOrderId,
        this.lastDeepLinkOrderIdParam,
      ])
    ) {
      return;
    }

    const dedupeId = detail.payment_intent_id || incoming;
    const key = `${dedupeId}-succeeded`;
    if (this.processedPayments.has(key)) {
      return;
    }
    this.processedPayments.add(key);
    setTimeout(() => this.processedPayments.delete(key), 5 * 60 * 1000);

    const emitOrderId = (this.paymentOrderId ?? '').replace(/^#+/, '').trim() || incoming;
    this.apiService
      .get(`order/${emitOrderId}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const order = unwrapOrderFromApiResponse(res) as Record<string, any> | null;
          const paymentStatus = String(order?.['payment_status'] ?? '').toLowerCase();
          if (paymentStatus !== 'paid') {
            this.onAndroidTapToPayPaymentFailed({
              status: 'failed',
              order_id: incoming,
              internal_id: emitOrderId,
              message:
                String(order?.['failure_message'] ?? '').trim() ||
                'Payment was not completed. Please try again.',
            });
            return;
          }
          this.tapToPayHoldOverlayUntilSuccess = true;
          this.stopStatusPolling(false);
          this.disconnectTapToPayPusher();
          this.emitPaymentSuccess(emitOrderId, {
            status: 'succeeded',
            payment_intent_id: detail.payment_intent_id,
            order_id: incoming,
            source: 'tap_to_pay_android_webview',
          });
        },
        error: () => {
          this.onAndroidTapToPayPaymentFailed({
            status: 'failed',
            order_id: incoming,
            internal_id: emitOrderId,
            message: 'Could not verify payment status. Please check the order.',
          });
        },
      });
  }

  private onAndroidTapToPayPaymentFailed(detail: TapToPayAndroidPaymentDetail): void {
    if (!this.isGoingToPayment || detail.status !== 'failed') {
      return;
    }
    const incoming = (detail.order_id ?? '').replace(/^#+/, '').trim();
    const incomingInternal = (detail.internal_id ?? '').replace(/^#+/, '').trim();
    const internal = (this.paymentOrderId ?? '').replace(/^#+/, '').trim();
    const fromLink = (this.lastDeepLinkOrderIdParam ?? '').replace(/^#+/, '').trim();
    const hasIncoming = !!(incoming || incomingInternal);
    const matched =
      !hasIncoming ||
      (!!internal && (incoming === internal || incomingInternal === internal)) ||
      (!!fromLink && (incoming === fromLink || incomingInternal === fromLink));
    if (!matched) {
      return;
    }
    const emitOrderId = internal || incoming || incomingInternal || fromLink;
    const msg = (detail.message ?? '').trim() || 'Payment could not be processed.';
    this.tapToPayHoldOverlayUntilSuccess = false;
    this.stopStatusPolling();
    this.disconnectTapToPayPusher();
    this.isGoingToPayment = false;
    this.paymentOrderId = null;
    this.taptopayLoading = false;
    this.paymentFailed$.next({ orderId: emitOrderId, message: msg });
  }

  /**
   * Builds URL search parameters for a Tap to Pay order request from API order data and context.
   * @example
   * buildOrderParams(apiOrder, ctx)
   * new URLSearchParams("amount=25.00&orderId=123&currency=USD")
   * @param {any} apiOrder - Order data returned from the API.
   * @param {TapToPayContext} ctx - Context values used to fill in missing order fields.
   * @returns {URLSearchParams} URLSearchParams containing normalized order and customer parameters.
   */
  private buildOrderParams(apiOrder: any, ctx: TapToPayContext): URLSearchParams {
    return buildTapToPayNativeParams({
      apiOrder: apiOrder as Record<string, any>,
      fallbackOrderId: resolveInternalOrderId(apiOrder, ''),
      source: 'saas',
      amount: this.cleanAmount(apiOrder?.final_price ?? '0.00'),
      customerId: apiOrder?.customer_id ?? apiOrder?.customerId,
      locationId: apiOrder?.location_id ?? ctx.locationId,
      adminUserId: apiOrder?.user_id ?? ctx.adminUserId,
      washType: apiOrder?.wash_type ?? ctx.washType,
      packageId: apiOrder?.package_id ?? ctx.packageId,
      vehicleId: apiOrder?.vehicle_id ?? ctx.vehicleId,
      stripeAccountId: apiOrder?.stripe_account_id,
      phoneNumber: ctx.phoneNumber,
      email: ctx.email,
      authToken: ctx.authToken,
    });
  }

  /**
   * Builds URL search parameters for a Tap to Pay payment request using the provided context.
   * @example
   * buildFormParams(ctx)
   * URLSearchParams
   * @param {TapToPayContext} ctx - Context containing payment details and optional customer/order fields.
   * @returns {URLSearchParams} URL-encoded parameters for submitting the Tap to Pay form.
   */
  private buildFormParams(ctx: TapToPayContext): URLSearchParams {
    return buildTapToPayNativeParams({
      apiOrder: null,
      fallbackOrderId: '',
      source: 'saas',
      amount: ctx.amount,
      locationId: ctx.locationId,
      adminUserId: ctx.adminUserId,
      washType: ctx.washType,
      packageId: ctx.packageId,
      vehicleId: ctx.vehicleId,
      stripeAccountId: ctx.stripeAccountId,
      phoneNumber: ctx.phoneNumber,
      email: ctx.email,
      authToken: ctx.authToken,
    });
  }

  /**
   * Launches the Tap to Pay deep link flow, updates internal loading/polling state, and optionally subscribes for location updates.
   * @example
   * launchDeepLink(params, internalOrderId, locationId)
   * void
   * @param {URLSearchParams} params - URL search parameters used to build the deep link and fallback URL.
   * @param {string | null} internalOrderId - Internal order identifier used for polling and SSE subscription.
   * @param {string} locationId - Location identifier used to subscribe to location-specific updates.
   * @returns {void} Does not return a value.
   **/
  private launchDeepLink(params: URLSearchParams, internalOrderId: string | null, locationId: string): void {
    this.lastDeepLinkOrderIdParam =
      (params.get('orderId') ?? '').replace(/^#+/, '').trim() || null;

    this.tapToPayHoldOverlayUntilSuccess = false;
    this.isGoingToPayment = true;
    this.paymentOrderId = internalOrderId;
    this.taptopayLoading = true;
    this.startStatusPolling(internalOrderId ?? '', 5000, 120000);

    if (locationId && internalOrderId) {
      this.subscribePusherForLocation(locationId, internalOrderId);
    }

    const orderId = params.get('orderId') ?? '';
    if (orderId) {
      this.apiService
        .put(`order/tap-to-pay-status/${orderId}`, { tap_to_pay_status: 'app_opened' })
        .pipe(takeUntil(this.destroy$))
        .subscribe({ error: (e: any) => this.logger.trackError('TapToPayService.statusUpdate', e) });
    }

    this.sendTapToPayToNativeApp(params);
  }

  private sendTapToPayToNativeApp(params: URLSearchParams): void {
    if (!this.tapToPayAndroidNative.isAvailable) {
      console.warn('[TapToPay] Native Android shell required (TapToPayAndroid bridge missing).');
      return;
    }
    this.tapToPayAndroidNative.startPaymentFromParams(params);
  }

  /**
   * Polls the backend for an order's payment status and emits success or failure events.
   * @example
   * pollOrderStatus('order_123')
   * void
   * @param {string} orderId - The ID of the order to poll.
   * @returns {void} No return value.
   **/
  private pollOrderStatus(orderId: string): void {
    if (!orderId) return;
    this.apiService
      .get(`order/${orderId}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const order = unwrapOrderFromApiResponse(res) as any;
          const status = (order?.payment_status ?? '').toLowerCase();
          if (status === 'paid' || status === 'succeeded') {
            this.tapToPayHoldOverlayUntilSuccess = true;
            this.stopStatusPolling(false);
            this.emitPaymentSuccess(orderId, order);
          } else if (status === 'failed') {
            this.tapToPayHoldOverlayUntilSuccess = false;
            this.stopStatusPolling();
            this.paymentFailed$.next({ orderId, message: order?.payment_failure_message ?? '' });
          }
        },
      });
  }

  /**
   * Handles a successful payment event, deduplicates repeated success notifications, and emits the payment success payload.
   * @example
   * handleSuccess('payment_intent.succeeded', data)
   * void
   * @param {string} event - The payment event name used to build a deduplication key when no payment ID is available.
   * @param {any} data - The payment response payload containing payment/order identifiers and status information.
   * @returns {void} Nothing is returned.
   **/
  private handleSuccess(event: string, data: any): void {
    const paymentId = data?.payment_intent_id ?? data?.payment_intent ?? data?.order_id ?? data?.orderId;
    const key = paymentId ? `${paymentId}-succeeded` : `${event}-${Date.now()}`;
    if (this.processedPayments.has(key)) return;
    this.processedPayments.add(key);
    setTimeout(() => this.processedPayments.delete(key), 5 * 60 * 1000);

    if (data?.status === 'succeeded') {
      const orderId = data?.order_id ?? data?.orderId ?? paymentId ?? '';
      this.tapToPayHoldOverlayUntilSuccess = true;
      this.stopStatusPolling(false);
      this.emitPaymentSuccess(this.paymentOrderId ?? orderId.toString(), data);
    }
  }

  /**
  * Handles a failed tap-to-pay payment event, deduplicates repeated failures, and emits a failure notification when applicable.
  * @example
  * handleFailure('payment_failed', { payment_intent_id: 'pi_123', status: 'failed', message: 'Payment declined' })
  * undefined
  * @param {string} event - The event name associated with the failure.
  * @param {any} data - Payload containing payment or order details and failure status.
  * @returns {void} This method does not return a value.
  **/
  private handleFailure(event: string, data: any): void {
    const paymentId = data?.payment_intent_id ?? data?.order_id ?? data?.orderId;
    const key = paymentId ? `${paymentId}-failed` : `${event}-${Date.now()}`;
    if (this.processedPayments.has(key)) return;
    this.processedPayments.add(key);
    setTimeout(() => this.processedPayments.delete(key), 5 * 60 * 1000);

    if (data?.status === 'failed') {
      const orderId = data?.order_id ?? data?.orderId ?? '';
      this.stopStatusPolling();
      this.taptopayLoading = false;
      this.paymentFailed$.next({ orderId: orderId.toString(), message: data?.message ?? '' });
    }
  }

  /**
   * Stripe Terminal may succeed before the order is scheduled; confirm `card_present` then notify UI.
   */
  private emitPaymentSuccess(orderId: string, data: any): void {
    const internalId =
      resolveInternalOrderId(null, orderId) ||
      (this.paymentOrderId ?? '').replace(/^#+/, '').trim() ||
      String(orderId ?? '').replace(/^#+/, '').trim();
    if (!internalId) {
      return;
    }

    confirmTapToPayPaymentMode(this.apiService, internalId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.logger.trackPayment('Tap to Pay confirmed (add order)', {
            orderId: internalId,
            paymentMode: PaymentMethodCode.CardPresent,
          });
          this.paymentSuccess$.next({ orderId: internalId, data });
        },
        error: (error) => {
          this.logger.trackError('TapToPayService.confirmPaymentMode', error, { orderId: internalId });
          this.paymentSuccess$.next({ orderId: internalId, data });
        },
      });
  }

  private cleanAmount(raw: string): string {
    const n = parseFloat((raw ?? '0').replace(/[$,]/g, '').trim()) || 0;
    return n.toString();
  }

  private cleanPhone(phone: string): string {
    return (phone ?? '').replace(/[\s\-()]/g, '');
  }

  private setIfPresent(params: URLSearchParams, key: string, value: any): void {
    const str = (value ?? '').toString().trim();
    if (str) params.set(key, str);
  }
}
